import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

function getAddonVariantId(card) {
  const productHandle = card.querySelector('.addon-toggle').dataset.productHandle;
  const productData = window.addonProducts[productHandle];
  if (!productData) return null;

  const selectedOptions = [];

  card.querySelectorAll('.addon-option').forEach(select => {
    selectedOptions.push(select.value.trim());
  });

  const variant = productData.variants.find(v => {
    return v.options.every((opt, index) => {
      return opt === selectedOptions[index];
    });
  });

  return variant ? variant.id : null;
}
// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }


  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */

  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    // If addon-products.js is managing this section's cart submission, bail out
    // immediately so we don't double-add the main product.
    // addon-products.js registers its own form-level capture listener that fires
    // after this document-level capture listener and handles the full submission.
    const sectionId = this.dataset.sectionId;
    if (sectionId && document.querySelector(`[data-addon-manager][data-section-id="${sectionId}"]`)) {
      event.preventDefault();
      return;
    }

    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);


    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    // fetch(Theme.routes.cart_add_url, {
    //   ...fetchCfg,
    //   headers: {
    //     ...fetchCfg.headers,
    //     Accept: 'text/html',
    //   },
    // })
    //   .then((response) => response.json())
    //   .then(async (response) => {
    //     if (response.status) {
    //       this.dispatchEvent(
    //         new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
    //       );

    //       if (!addToCartTextError) return;
    //       addToCartTextError.classList.remove('hidden');

    //       // Reuse the text node if the user is spam-clicking
    //       const textNode = addToCartTextError.childNodes[2];
    //       if (textNode) {
    //         textNode.textContent = response.message;
    //       } else {
    //         const newTextNode = document.createTextNode(response.message);
    //         addToCartTextError.appendChild(newTextNode);
    //       }

    //       // Create or get existing error live region for screen readers
    //       this.#setLiveRegionText(response.message);

    //       this.#timeout = setTimeout(() => {
    //         if (!addToCartTextError) return;
    //         addToCartTextError.classList.add('hidden');

    //         // Clear the announcement
    //         this.#clearLiveRegionText();
    //       }, ERROR_MESSAGE_DISPLAY_DURATION);

    //       // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
    //       // because our back-end still adds the max allowed amount to the cart.
    //       this.dispatchEvent(
    //         new CartAddEvent({}, this.id, {
    //           didError: true,
    //           source: 'product-form-component',
    //           itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
    //           productId: this.dataset.productId,
    //         })
    //       );

    //       return;
    //     } else {
    //       const id = formData.get('id');

    //       if (addToCartTextError) {
    //         addToCartTextError.classList.add('hidden');
    //         addToCartTextError.removeAttribute('aria-live');
    //       }

    //       if (!id) throw new Error('Form ID is required');

    //       // Add aria-live region to inform screen readers that the item was added
    //       // Get the added text from any add-to-cart button
    //       const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
    //       if (anyAddToCartButton) {
    //         const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
    //         const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

    //         this.#setLiveRegionText(addedText);

    //         setTimeout(() => {
    //           this.#clearLiveRegionText();
    //         }, SUCCESS_MESSAGE_DISPLAY_DURATION);
    //       }

    //       // Fetch the updated cart to get the actual total quantity for this variant
    //       await this.#fetchAndUpdateCartQuantity();

    //       this.dispatchEvent(
    //         new CartAddEvent({}, id.toString(), {
    //           source: 'product-form-component',
    //           itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
    //           productId: this.dataset.productId,
    //           sections: response.sections,
    //         })
    //       );
    //     }
    //   })
    //   .catch((error) => {
    //     console.error(error);
    //   })
    //   .finally(() => {
    //     cartPerformance.measureFromEvent('add:user-action', event);
    //   });
    const mainVariantId = Number(formData.get('id'));
    const mainQty =
    Number(formData.get('quantity')) ||
    Number(this.dataset.quantityDefault);

    // const items = [
    //   {
    //     id: mainVariantId,
    //     quantity: mainQty
    //   }
    // ];
    const items = [
  {
    id: mainVariantId,
    quantity: mainQty,
    properties: {
      _main_product: true
    }
  }
];

    // ================= COLLECT ADDONS =================
    // ================= COLLECT ADDONS =================
    this.querySelectorAll('.addon-card').forEach((card) => {
      const checkbox = card.querySelector('.addon-toggle');
      if (!checkbox || !checkbox.checked) return;
      

      const variantId = Number(checkbox.value);
      const quantity =
        Number(card.querySelector('.addon-qty-input')?.value) || 1;

      const properties = {};

      // // ✅ Variant options ONLY
      // card.querySelectorAll('.addon-option').forEach((select) => {
      //   if (!select.value) return;

      //   const optionName =
      //     select.dataset.optionName || select.name || 'Option';

      //   properties[optionName] = select.value;
      // });

      // ✅ Engraving text
      const engravingText =
        card.querySelector('.addon-engraving-text')?.value;
      if (engravingText) {
        properties['Engraving Text'] = engravingText;
      }

      // ✅ Font (ONLY ONCE)
      const font =
        card.querySelector('.addon-font-select')?.value;
      if (font) {
        properties['Font'] = font;
      }

      // items.push({
      //   id: variantId,
      //   quantity,
      //   properties
      // });
      items.push({
        id: variantId,
        quantity,
        properties: {
          ...properties,
          _addon_product: true,
          _addon_for: mainVariantId
        }
      });
    });

    // ================= FINAL FETCH =================
    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items })
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.status) {
          // console.error(response);
          // return;
          if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');

        const textNode = addToCartTextError.childNodes[2];

        if (textNode) {
          textNode.textContent = response.message;
        } else {
          const newTextNode = document.createTextNode(response.message);
          addToCartTextError.appendChild(newTextNode);
        }

        this.#setLiveRegionText(response.message);

        this.#timeout = setTimeout(() => {
          addToCartTextError.classList.add('hidden');
          this.#clearLiveRegionText();
        }, ERROR_MESSAGE_DISPLAY_DURATION);
      }

        }

        this.dispatchEvent(
          new CartAddEvent({}, mainVariantId.toString(), {
            source: 'product-form-component',
            itemCount: mainQty,
            productId: this.dataset.productId
          })
        );
      })
      .catch(console.error)
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}


document.addEventListener('DOMContentLoaded', () => {
  // Main product quantity input
  // const mainQtyInput = document.querySelector('quantity-selector-component input[name="quantity"]');
  // if (!mainQtyInput) return;

  

  // const attachEngravingLimit = (qtyInput) => {
  //   const card = qtyInput.closest('.addon-card');
  //   if (!card) return;

  //   const isEngraving = card.querySelector('.addon-engraving-text');
  //   if (!isEngraving) return;

  //   qtyInput.removeEventListener('input', qtyInput._engravingListener);

  //   const listener = () => {
  //     const mainQty = Number(mainQtyInput.value) || 1;

  //     // ✅ Get ALL engraving qty inputs
  //     const allEngravingInputs = document.querySelectorAll(
  //       '.addon-card .addon-engraving-text'
  //     );

  //     let total = 0;

  //     allEngravingInputs.forEach((engravingField) => {
  //       const parentCard = engravingField.closest('.addon-card');
  //       const qtyField = parentCard.querySelector('.addon-qty-input');

  //       if (qtyField) {
  //         total += Number(qtyField.value) || 0;
  //       }
  //     });

  //     // ✅ If total exceeds main qty, fix current input
  //     if (total > mainQty) {
  //       const currentVal = Number(qtyInput.value) || 0;
  //       const excess = total - mainQty;

  //       qtyInput.value = Math.max(currentVal - excess, 0);
  //     }
  //   };

  //   qtyInput.addEventListener('input', listener);
  //   qtyInput._engravingListener = listener;
  // };

  // // 1️⃣ Initial existing engraving inputs
  // document.querySelectorAll('.addon-card input.addon-qty-input').forEach(attachEngravingLimit);
  

  // // 2️⃣ Observe DOM for dynamically added clones
  // const observer = new MutationObserver((mutations) => {
  //   mutations.forEach((mutation) => {
  //     mutation.addedNodes.forEach((node) => {
  //       if (!(node instanceof HTMLElement)) return;

  //       // If a new addon-card clone added
  //       const qtyInputs = node.querySelectorAll('.addon-card input.addon-qty-input');
  //       qtyInputs.forEach(attachEngravingLimit);
  //     });
  //   });
  // });

  // observer.observe(document.querySelector('.addons-wrapper'), {
  //   childList: true,
  //   subtree: true
  // });
  const mainQtyInput = document.querySelector('quantity-selector-component input[name="quantity"]');
if (!mainQtyInput) return;

const attachAddonLogic = (qtyInput) => {

  const card = qtyInput.closest('.addon-card');
  if (!card) return;

  const isEngraving = card.querySelector('.addon-engraving-text');

  qtyInput.removeEventListener('input', qtyInput._addonListener);

  const listener = () => {

    const mainQty = Number(mainQtyInput.value) || 1;

    // ===============================
    // ENGRAVING PRODUCTS LOGIC
    // ===============================
    if (isEngraving) {

      const productItem = card.closest('.addon-card-products-item');

      const allEngravingInputs = productItem.querySelectorAll('.addon-card .addon-engraving-text');

      let total = 0;

      allEngravingInputs.forEach((engravingField) => {

        const parentCard = engravingField.closest('.addon-card');
        const qtyField = parentCard.querySelector('.addon-qty-input');

        if (qtyField) {
          total += Number(qtyField.value) || 0;
        }

      });

      if (total > mainQty) {
        const currentVal = Number(qtyInput.value) || 0;
        const excess = total - mainQty;

        qtyInput.value = Math.max(currentVal - excess, 0);
      }

    } 
    // ===============================
    // NORMAL ADDON PRODUCTS
    // ===============================
    else {

      if (Number(qtyInput.value) > mainQty) {
        qtyInput.value = mainQty;
      }

    }

  };

  qtyInput.addEventListener('input', listener);
  qtyInput._addonListener = listener;
};


// 1️⃣ Existing addons
document.querySelectorAll('.addon-card input.addon-qty-input')
.forEach(attachAddonLogic);


// 2️⃣ Observe dynamically added addons
const observer = new MutationObserver((mutations) => {

  mutations.forEach((mutation) => {

    mutation.addedNodes.forEach((node) => {

      if (!(node instanceof HTMLElement)) return;

      const qtyInputs = node.querySelectorAll('.addon-card input.addon-qty-input');

      qtyInputs.forEach(attachAddonLogic);

    });

  });

});
// Guard: `.addons-wrapper` may not exist in the DOM (the rendered add-ons
// container is `.addon-products-wrapper`). MutationObserver.observe() throws a
// TypeError if passed null, so only observe when the element is present.
const addonsWrapper = document.querySelector('.addons-wrapper');
if (addonsWrapper) {
  observer.observe(addonsWrapper, {
    childList: true,
    subtree: true
  });
}
});


  document.addEventListener('click', function (e) {
  const addMoreBtn = e.target.closest('label.add_on_button.addmore');
  if (!addMoreBtn) return;

  const addonCard = addMoreBtn.closest('.addon-card');
  const productItem = addMoreBtn.closest('.addon-card-products-item');

  if (!addonCard || !productItem) return;

  const clone = addonCard.cloneNode(true);

  // reset text fields
  clone.querySelectorAll('input[type="text"]').forEach(i => i.value = '');

  // reset number
  clone.querySelectorAll('input[type="number"]').forEach(i => i.value = 1);

  // reset selects
  clone.querySelectorAll('select').forEach(s => s.selectedIndex = 0);
  

  // reset checkbox + variant id
  clone.querySelectorAll('.addon-toggle').forEach(c => {
    c.checked = false;
    c.classList.remove('added');

    const variantId = getAddonVariantId(clone);
    if (variantId) {
      c.value = variantId;
    }
  });

  // remove added class from card
  clone.classList.remove('added');

  // fix duplicate id
  const checkbox = clone.querySelector('.addon-toggle');
  if (checkbox) {
    const uniqueId = 'addon-' + Date.now() + Math.floor(Math.random() * 1000);
    checkbox.id = uniqueId;

    clone.querySelectorAll('label[for]').forEach(label => {
      label.setAttribute('for', uniqueId);
    });
  }

  productItem.appendChild(clone);
    const newAddBtn = clone.querySelector('.addon-toggle');
    if (newAddBtn) {
      newAddBtn.setAttribute('disabled', true);
    }
  updateAddMoreButton();
});





  document.addEventListener('change', function (e) {
    const checkbox = e.target.closest('.addon-toggle');
    if (!checkbox) return;

    checkbox.classList.toggle('added', checkbox.checked);
  });

  document.addEventListener('click', function (e) {
    const removeBtn = e.target.closest('.addon-remove-button'); // update selector
    if (!removeBtn) return;

    // 1️⃣ Add removed-item class to closest addon-card
    const addonCard = removeBtn.closest('.addon-card');
    if (!addonCard) return;
    addonCard.classList.add('removed-item');

    // 2️⃣ Wait for CSS animation to finish (optional: 300ms)
    addonCard.addEventListener('transitionend', function handleTransitionEnd() {
      // 3️⃣ Remove from DOM the parent addon-card-products-item
      // const productItem = addonCard.closest('.addon-card-products-item');
      // if (productItem) {
        addonCard.remove();
        updateAddMoreButton();
      // }

      // Clean up event listener
      addonCard.removeEventListener('transitionend', handleTransitionEnd);
    });
    

});
document.addEventListener('input', function(e){

  if(e.target.classList.contains('addon-qty-input')){
    updateAddMoreButton();
  }

});
document.addEventListener('click', function(e){

  if(
    e.target.closest('.quantity-plus') ||
    e.target.closest('.quantity-minus')
  ){
    setTimeout(() => {
      updateAddMoreButton();
      syncNormalAddonQty(); // ✅ ADD THIS
    }, 100);
  }

});
document.addEventListener('QuantitySelectorUpdateEvent', function () {
  updateAddMoreButton();
  syncNormalAddonQty();
});


function updateAddMoreButton() {

  const mainQtyInput = document.querySelector('quantity-selector-component input[name="quantity"]');
  if (!mainQtyInput) return;

  const mainQty = Number(mainQtyInput.value) || 1;

  document.querySelectorAll('.addon-card-products-item').forEach(productItem => {

    const engravingCards = productItem.querySelectorAll('.addon-card');

    let engravingCardsArray = [];

    engravingCards.forEach(card => {
      const engravingField = card.querySelector('.addon-engraving-text');
      const qtyField = card.querySelector('.addon-qty-input');

      if (engravingField && qtyField) {
        engravingCardsArray.push({
          card: card,
          qty: Number(qtyField.value) || 0
        });
      }
    });

    let totalEngravingQty = engravingCardsArray.reduce((sum, item) => sum + item.qty, 0);

    // Remove extra cards if qty exceeds
    while (totalEngravingQty > mainQty && engravingCardsArray.length > 0) {

        // ✅ if one engraving available
        if (engravingCardsArray.length === 1) {

          const only = engravingCardsArray[0];
          const qtyField = only.card.querySelector('.addon-qty-input');

          if (qtyField) {
            qtyField.value = mainQty;
          }

          break;
        }

        // ✅ if one engraving cards available
        const last = engravingCardsArray.pop();
        last.card.remove();

        totalEngravingQty -= last.qty;
      }

    // 1️⃣ Hide all Add More buttons inside this product
    productItem.querySelectorAll('.add_on_button.addmore').forEach(btn => {
      btn.style.display = "none";
    });

    // 2️⃣ Find last engraving card
    const lastCard = engravingCardsArray.length
      ? engravingCardsArray[engravingCardsArray.length - 1].card
      : null;

    // 3️⃣ Show button only on last card
    if (lastCard && totalEngravingQty < mainQty) {
      const btn = lastCard.querySelector('.add_on_button.addmore');
      if (btn) {
        btn.style.display = "inline-block";
      }
    }

  });

}
function syncNormalAddonQty() {

  const mainQtyInput = document.querySelector('quantity-selector-component input[name="quantity"]');
  if (!mainQtyInput) return;

  const mainQty = Number(mainQtyInput.value) || 1;

  document.querySelectorAll('.addon-card').forEach(card => {

    const engravingField = card.querySelector('.addon-engraving-text');
    const qtyField = card.querySelector('.addon-qty-input');

    // ❌ engraving skip
    if (engravingField) return;

    if (qtyField) {

      const addonQty = Number(qtyField.value) || 1;

      // ✅ only limit
      if (addonQty > mainQty) {
        qtyField.value = mainQty;
      }

    }

  });

}

document.addEventListener('DOMContentLoaded', () => {
  updateAddMoreButton();
  syncNormalAddonQty();
  validateEngravingCards();
  // addon related code ...



  document.addEventListener('change', function(e){

  if(!e.target.classList.contains('addon-option')) return;

  const card = e.target.closest('.addon-card');
  if(!card) return;

  const variantId = getAddonVariantId(card);
  if(!variantId) return;

  const checkbox = card.querySelector('.addon-toggle');

  checkbox.value = variantId;

});

});

function validateEngravingCards() {

  document.querySelectorAll('.addon-card').forEach(card => {

    const engravingText = card.querySelector('.addon-engraving-text');
    const fontSelect = card.querySelector('.addon-font-select'); // font dropdown
    const variantSelect = card.querySelector('.addon-option'); // variant dropdown
    const checkbox = card.querySelector('.addon-toggle');

    if (!engravingText || !fontSelect || !variantSelect || !checkbox) return;

    const textFilled = engravingText.value.trim() !== '';
    const fontSelected = fontSelect.value !== '';
    const variantSelected = variantSelect.value !== '';

    if (textFilled && fontSelected && variantSelected) {
      checkbox.disabled = false;
    } else {
      checkbox.disabled = true;
    }

  });

}
document.addEventListener('input', function(e){

  if(
    e.target.classList.contains('addon-engraving-text') ||
    e.target.classList.contains('addon-option') ||
    e.target.classList.contains('addon-font-select')
  ){
    validateEngravingCards();
  }

});